import { getApiErrorMessage } from '@/lib/utils';
import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Lock, Bell, Phone, Palette, Eye, EyeOff, Save, RefreshCw, HardDrive, CloudOff, Cloud, ShieldCheck, Smartphone, Users } from 'lucide-react';
import MobileShortcutsTab from '@/components/settings/MobileShortcutsTab';
import NotificationsSettingsTab from '@/components/settings/NotificationsSettingsTab';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient as api } from '@/lib/api';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { SectionNav, type SectionNavSectionDef } from '@easyflow/ui-frontend/components/ui/section-nav';
import { useSectionNavState } from '@/hooks/useSectionNavState';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Separator } from '@easyflow/ui-frontend/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { Progress } from '@easyflow/ui-frontend/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import PersonVisibilityPolicyTab from '@/components/settings/PersonVisibilityPolicyTab';

interface StorageSettings {
  recordings_enabled: boolean;
  recordings_max_bytes: number;
  recordings_retention_days: number;
  alert_threshold_pct: number;
}

const DEFAULT_STORAGE_SETTINGS: StorageSettings = {
  recordings_enabled: true,
  recordings_max_bytes: 3221225472,
  recordings_retention_days: 90,
  alert_threshold_pct: 80,
};

interface BookingSecuritySettings {
  require_otp_chatbot: boolean;
  require_otp_voicebot: boolean;
  otp_whatsapp_template_id: string | null;
}

interface OtpTemplate {
  id: string;
  friendly_name: string;
  category: string;
  language: string;
  body_text: string;
}

// Le route storage rispondono con envelope arricchito: campi settings/quota
// al TOP LEVEL accanto a `success` (non sotto `data`).
interface StorageSettingsResponse {
  success?: boolean;
  settings?: StorageSettings;
}
interface StorageQuotaResponse {
  success?: boolean;
  quota?: { used_bytes?: number; max_bytes?: number };
}

const STORAGE_SIZE_OPTIONS = [
  { value: 536870912, label: '512 MB' },
  { value: 1073741824, label: '1 GB' },
  { value: 2147483648, label: '2 GB' },
  { value: 3221225472, label: '3 GB' },
  { value: 5368709120, label: '5 GB' },
  { value: 10737418240, label: '10 GB' },
  { value: 21474836480, label: '20 GB' },
  { value: 53687091200, label: '50 GB' },
  { value: 107374182400, label: '100 GB' },
];

const RETENTION_OPTIONS = [
  { value: 7, label: '7 giorni' },
  { value: 14, label: '14 giorni' },
  { value: 30, label: '30 giorni' },
  { value: 60, label: '60 giorni' },
  { value: 90, label: '90 giorni' },
  { value: 180, label: '180 giorni' },
  { value: 365, label: '1 anno' },
];

type SettingsTabValue =
  | 'account'
  | 'appearance'
  | 'notifications'
  | 'mobile-shortcuts'
  | 'sip'
  | 'booking-security'
  | 'visibilita-persone'
  | 'storage';

function buildSettingsSections(isAdmin: boolean): SectionNavSectionDef[] {
  const sections: SectionNavSectionDef[] = [
    { value: 'account', label: 'Account', icon: Lock },
    {
      value: 'preferenze',
      label: 'Preferenze',
      icon: Palette,
      subs: [
        { value: 'appearance', label: 'Aspetto', icon: Palette },
        { value: 'notifications', label: 'Notifiche', icon: Bell },
        { value: 'mobile-shortcuts', label: 'Shortcut mobile', icon: Smartphone },
      ],
    },
    {
      value: 'comunicazioni',
      label: 'Comunicazioni',
      icon: Phone,
      subs: [
        { value: 'sip', label: 'SIP', icon: Phone },
      ],
    },
  ];
  if (isAdmin) {
    sections.push({
      value: 'sicurezza',
      label: 'Sicurezza',
      icon: ShieldCheck,
      subs: [
        { value: 'booking-security', label: 'Sicurezza prenotazioni', icon: ShieldCheck },
        { value: 'visibilita-persone', label: 'Chi vede chi', icon: Users },
      ],
    });
    sections.push({ value: 'storage', label: 'Storage', icon: HardDrive });
  }
  return sections;
}

const NAV_TO_TAB: Record<string, SettingsTabValue> = {
  account: 'account',
  storage: 'storage',
  appearance: 'appearance',
  notifications: 'notifications',
  'mobile-shortcuts': 'mobile-shortcuts',
  sip: 'sip',
  'booking-security': 'booking-security',
  'visibilita-persone': 'visibilita-persone',
};

const TAB_TO_NAV: Record<SettingsTabValue, { section: string; sub?: string }> = {
  account: { section: 'account' },
  appearance: { section: 'preferenze', sub: 'appearance' },
  notifications: { section: 'preferenze', sub: 'notifications' },
  'mobile-shortcuts': { section: 'preferenze', sub: 'mobile-shortcuts' },
  sip: { section: 'comunicazioni', sub: 'sip' },
  'booking-security': { section: 'sicurezza', sub: 'booking-security' },
  'visibilita-persone': { section: 'sicurezza', sub: 'visibilita-persone' },
  storage: { section: 'storage' },
};

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export default function Settings() {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  
  const [storageSettings, setStorageSettings] = useState<StorageSettings>(DEFAULT_STORAGE_SETTINGS);
  const [isLoadingStorage, setIsLoadingStorage] = useState(false);
  const [isSavingStorage, setIsSavingStorage] = useState(false);
  const [storageQuota, setStorageQuota] = useState<{ used_bytes: number; max_bytes: number } | null>(null);

  const [bookingSecurity, setBookingSecurity] = useState<BookingSecuritySettings>({ require_otp_chatbot: false, require_otp_voicebot: false, otp_whatsapp_template_id: null });
  const [otpTemplates, setOtpTemplates] = useState<OtpTemplate[]>([]);
  const [isLoadingBookingSec, setIsLoadingBookingSec] = useState(false);
  const [isSavingBookingSec, setIsSavingBookingSec] = useState(false);

  const isAdmin = user?.role === 'admin';

  const settingsSections = React.useMemo(() => buildSettingsSections(isAdmin), [isAdmin]);
  const { value: navValue, setValue: setNavValue } = useSectionNavState({ sections: settingsSections });
  const tabValueFromNav: SettingsTabValue =
    (navValue.sub && NAV_TO_TAB[navValue.sub]) || NAV_TO_TAB[navValue.section] || 'account';
  const navFromTabValue = React.useCallback(
    (tab: string): { section: string; sub?: string } => TAB_TO_NAV[tab as SettingsTabValue] || { section: 'account' },
    [],
  );
  
  // SIP settings utente: solo preferenze locali. Il WSS server è autoritario backend
  // (env SIP_PUBLIC_WSS_URL via /voip/my-extension), non editabile da UI.
  // Cleanup chiave legacy una volta sola al mount (era localStorage[sip_ws_server]).
  useEffect(() => {
    localStorage.removeItem('sip_ws_server');
  }, []);

  const [sipSettings, setSipSettings] = useState({
    stunServer: 'stun:stun.l.google.com:19302',
    debugMode: false,
    autoAnswer: false,
    codec: 'opus'
  });

  const loadBookingSecurity = async () => {
    setIsLoadingBookingSec(true);
    try {
      const [settingsRes, templatesRes] = await Promise.all([
        api.get('/tenant/booking-security'),
        api.get('/tenant/booking-security/templates'),
      ]);
      if (settingsRes?.data) {
        setBookingSecurity(settingsRes.data as BookingSecuritySettings);
      }
      if (templatesRes?.data && Array.isArray(templatesRes.data)) {
        setOtpTemplates(templatesRes.data as OtpTemplate[]);
      }
    } catch {
    } finally {
      setIsLoadingBookingSec(false);
    }
  };

  const loadStorageSettings = async () => {
    setIsLoadingStorage(true);
    try {
      const [settingsRes, quotaRes] = await Promise.all([
        api.get('/files/storage/settings') as Promise<StorageSettingsResponse>,
        api.get('/files/storage/quota') as Promise<StorageQuotaResponse>,
      ]);
      if (settingsRes?.settings) {
        setStorageSettings(settingsRes.settings);
      }
      if (quotaRes?.quota) {
        setStorageQuota({
          used_bytes: quotaRes.quota.used_bytes || 0,
          max_bytes: quotaRes.quota.max_bytes || 0,
        });
      }
    } catch {
    } finally {
      setIsLoadingStorage(false);
    }
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
    if (savedTheme) {
      setTheme(savedTheme);
    }
    
    const savedStunServer = localStorage.getItem('sip_stun_server');
    const savedDebugMode = localStorage.getItem('sip_debug_mode');
    const savedAutoAnswer = localStorage.getItem('sip_auto_answer');
    const savedCodec = localStorage.getItem('sip_codec');

    setSipSettings({
      stunServer: savedStunServer || 'stun:stun.l.google.com:19302',
      debugMode: savedDebugMode === 'true',
      autoAnswer: savedAutoAnswer === 'true',
      codec: savedCodec || 'opus'
    });
    
    if (isAdmin) {
      loadStorageSettings();
      loadBookingSecurity();
    }
  }, [isAdmin]);

  const handleSaveBookingSecurity = async () => {
    setIsSavingBookingSec(true);
    try {
      const res = await api.put('/tenant/booking-security', bookingSecurity);
      if (res?.data) setBookingSecurity(res.data as BookingSecuritySettings);
      toast({ title: 'Sicurezza prenotazioni aggiornata', description: 'Le impostazioni OTP sono state salvate' });
    } catch (err) {
      toast({
        title: 'Errore',
        description: getApiErrorMessage(err) || 'Impossibile salvare le impostazioni',
        variant: 'destructive',
      });
    } finally {
      setIsSavingBookingSec(false);
    }
  };

  const handleSaveStorageSettings = async () => {
    setIsSavingStorage(true);
    try {
      const res = await api.patch('/files/storage/settings', storageSettings) as StorageSettingsResponse;
      if (res?.settings) {
        setStorageSettings(res.settings);
      }
      toast({
        title: "Impostazioni storage salvate",
        description: "Le impostazioni di storage sono state aggiornate"
      });
    } catch {
      toast({
        title: "Errore",
        description: "Impossibile salvare le impostazioni storage",
        variant: "destructive"
      });
    } finally {
      setIsSavingStorage(false);
    }
  };

  const handleThemeChange = (newTheme: 'light' | 'dark') => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
    toast({
      title: "Tema aggiornato",
      description: `Tema ${newTheme === 'dark' ? 'scuro' : 'chiaro'} attivato`
    });
  };

  const handlePasswordChange = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({
        title: "Errore",
        description: "Compila tutti i campi",
        variant: "destructive"
      });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast({
        title: "Errore",
        description: "Le password non coincidono",
        variant: "destructive"
      });
      return;
    }
    
    if (newPassword.length < 8) {
      toast({
        title: "Errore",
        description: "La password deve essere di almeno 8 caratteri",
        variant: "destructive"
      });
      return;
    }
    
    setIsChangingPassword(true);
    try {
      await api.post('/voip/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      toast({
        title: "Password aggiornata",
        description: "La tua password è stata cambiata con successo"
      });
      
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast({
        title: "Errore",
        description: getApiErrorMessage(error) || "Impossibile cambiare la password",
        variant: "destructive"
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleSaveSipSettings = () => {
    localStorage.setItem('sip_stun_server', sipSettings.stunServer);
    localStorage.setItem('sip_debug_mode', String(sipSettings.debugMode));
    localStorage.setItem('sip_auto_answer', String(sipSettings.autoAnswer));
    localStorage.setItem('sip_codec', sipSettings.codec);

    toast({
      title: "Impostazioni SIP salvate",
      description: "Ricarica la pagina per applicare le modifiche"
    });
  };

  const handleResetSipSettings = () => {
    const defaults = {
      stunServer: 'stun:stun.l.google.com:19302',
      debugMode: false,
      autoAnswer: false,
      codec: 'opus'
    };
    setSipSettings(defaults);
    localStorage.setItem('sip_stun_server', defaults.stunServer);
    localStorage.setItem('sip_debug_mode', 'false');
    localStorage.setItem('sip_auto_answer', 'false');
    localStorage.setItem('sip_codec', 'opus');

    toast({
      title: "Impostazioni resettate",
      description: "Impostazioni SIP riportate ai valori predefiniti"
    });
  };

  return (
    <div className="w-full p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
          <SettingsIcon className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Impostazioni</h1>
          <p className="text-sm text-muted-foreground">Gestisci le tue preferenze personali</p>
        </div>
      </div>

      <SectionNav
        sections={settingsSections}
        value={navValue}
        onChange={setNavValue}
        ariaLabel="Sezioni impostazioni"
        className="mb-4"
      />
      <Tabs value={tabValueFromNav} onValueChange={(v) => setNavValue(navFromTabValue(v))} className="space-y-6">
        <TabsList className="sr-only">
          <TabsTrigger value="account">Account</TabsTrigger>
          <TabsTrigger value="appearance">Aspetto</TabsTrigger>
          <TabsTrigger value="notifications">Notifiche</TabsTrigger>
          <TabsTrigger value="mobile-shortcuts">Shortcut mobile</TabsTrigger>
          <TabsTrigger value="sip">SIP</TabsTrigger>
          <TabsTrigger value="booking-security">Sicurezza prenotazioni</TabsTrigger>
          <TabsTrigger value="visibilita-persone">Chi vede chi</TabsTrigger>
          <TabsTrigger value="storage">Storage</TabsTrigger>
        </TabsList>

        <TabsContent value="account">
          <Card>
            <CardHeader>
              <CardTitle>Cambia Password</CardTitle>
              <CardDescription>
                Aggiorna la tua password per mantenere il tuo account sicuro
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current-password">Password Attuale</Label>
                <div className="relative">
                  <Input
                    id="current-password"
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Inserisci la password attuale"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">Nuova Password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Inserisci la nuova password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Conferma Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Conferma la nuova password"
                />
              </div>

              <Button 
                onClick={handlePasswordChange} 
                disabled={isChangingPassword}
                className="w-full sm:w-auto"
              >
                {isChangingPassword ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Aggiornamento...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Cambia Password
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="appearance">
          <Card>
            <CardHeader>
              <CardTitle>Aspetto</CardTitle>
              <CardDescription>
                Personalizza l'aspetto dell'applicazione
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label>Tema</Label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => handleThemeChange('light')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      theme === 'light' 
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-12 h-8 rounded bg-white border shadow-sm" />
                    </div>
                    <span className="text-sm font-medium">Chiaro</span>
                  </button>
                  
                  <button
                    onClick={() => handleThemeChange('dark')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      theme === 'dark' 
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-12 h-8 rounded bg-gray-900 border border-gray-700" />
                    </div>
                    <span className="text-sm font-medium">Scuro</span>
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications">
          <NotificationsSettingsTab userRole={user?.role} />
        </TabsContent>

        <TabsContent value="mobile-shortcuts">
          <MobileShortcutsTab />
        </TabsContent>

        <TabsContent value="sip">
          <Card>
            <CardHeader>
              <CardTitle>Impostazioni SIP</CardTitle>
              <CardDescription>
                Configura la connessione WebRTC per le chiamate
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="stun-server">Server STUN</Label>
                <Input
                  id="stun-server"
                  value={sipSettings.stunServer}
                  onChange={(e) => setSipSettings(s => ({ ...s, stunServer: e.target.value }))}
                  placeholder="stun:stun.l.google.com:19302"
                />
                <p className="text-xs text-muted-foreground">Server STUN per la risoluzione NAT</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="codec">Codec Audio</Label>
                <select
                  id="codec"
                  value={sipSettings.codec}
                  onChange={(e) => setSipSettings(s => ({ ...s, codec: e.target.value }))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                >
                  <option value="opus">Opus (consigliato)</option>
                  <option value="g722">G.722</option>
                  <option value="pcmu">G.711 μ-law</option>
                  <option value="pcma">G.711 A-law</option>
                </select>
              </div>
              
              <Separator />
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Risposta automatica</Label>
                    <p className="text-sm text-muted-foreground">Rispondi automaticamente alle chiamate in arrivo</p>
                  </div>
                  <Switch
                    checked={sipSettings.autoAnswer}
                    onCheckedChange={(checked) => setSipSettings(s => ({ ...s, autoAnswer: checked }))}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Modalità debug</Label>
                    <p className="text-sm text-muted-foreground">Mostra log dettagliati nella console</p>
                  </div>
                  <Switch
                    checked={sipSettings.debugMode}
                    onCheckedChange={(checked) => setSipSettings(s => ({ ...s, debugMode: checked }))}
                  />
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={handleSaveSipSettings} className="flex-1 sm:flex-none">
                  <Save className="h-4 w-4 mr-2" />
                  Salva Impostazioni
                </Button>
                <Button variant="outline" onClick={handleResetSipSettings}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Ripristina Predefiniti
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="booking-security">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5" />
                  Sicurezza prenotazioni (OTP WhatsApp)
                </CardTitle>
                <CardDescription>
                  Richiedi la verifica via OTP WhatsApp per i nuovi clienti che prenotano tramite chatbot web o voicebot inbound.
                  Il codice viene inviato con un template Twilio approvato e scade dopo 5 minuti.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {isLoadingBookingSec ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">Richiedi OTP per chatbot web</p>
                        <p className="text-xs text-muted-foreground">
                          I nuovi clienti che prenotano tramite chatbot web devono verificare il numero via WhatsApp prima di creare l'appuntamento.
                        </p>
                      </div>
                      <Switch
                        checked={bookingSecurity.require_otp_chatbot}
                        onCheckedChange={(v) => setBookingSecurity((s) => ({ ...s, require_otp_chatbot: v }))}
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">Richiedi OTP per voicebot inbound</p>
                        <p className="text-xs text-muted-foreground">
                          I nuovi clienti che chiamano il voicebot devono leggere ad alta voce un codice ricevuto via WhatsApp prima che l'appuntamento venga creato.
                        </p>
                      </div>
                      <Switch
                        checked={bookingSecurity.require_otp_voicebot}
                        onCheckedChange={(v) => setBookingSecurity((s) => ({ ...s, require_otp_voicebot: v }))}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Template WhatsApp OTP</Label>
                      <Select
                        value={bookingSecurity.otp_whatsapp_template_id || '__none__'}
                        onValueChange={(val) =>
                          setBookingSecurity((s) => ({ ...s, otp_whatsapp_template_id: val === '__none__' ? null : val }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona un template approvato" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Nessun template —</SelectItem>
                          {otpTemplates.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.friendly_name} ({t.language}) — {t.category}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Vengono mostrati solo i template approvati con una variabile {'{{1}}'} per il codice (categoria AUTHENTICATION o con placeholder).
                        {otpTemplates.length === 0 && (
                          <span className="text-amber-600"> Nessun template approvato trovato. Creane uno nella sezione WhatsApp Templates.</span>
                        )}
                      </p>
                    </div>

                    <Separator />

                    <div className="rounded-lg bg-muted/30 p-4 text-xs space-y-1">
                      <p className="font-medium">Come funziona il flusso OTP</p>
                      <p>• Chatbot web: il chatbot chiede il numero, invia l'OTP, mostra un input, verifica e crea l'appuntamento.</p>
                      <p>• Voicebot inbound: il voicebot ripete il numero letto, invia il WhatsApp, il cliente legge il codice ad alta voce, il voicebot verifica.</p>
                      <p>• Max 3 richieste per numero per ora. Codice valido 5 minuti. Max 3 tentativi per codice, poi blocco temporaneo.</p>
                    </div>

                    <Button onClick={handleSaveBookingSecurity} disabled={isSavingBookingSec}>
                      {isSavingBookingSec ? (
                        <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Salvataggio...</>
                      ) : (
                        <><Save className="h-4 w-4 mr-2" />Salva Impostazioni</>
                      )}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="visibilita-persone">
            <PersonVisibilityPolicyTab />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="storage">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HardDrive className="h-5 w-5" />
                  Impostazioni Storage
                </CardTitle>
                <CardDescription>
                  Configura le sotto-quote e la retention per registrazioni e log cloud
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {isLoadingStorage ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <>
                    {storageQuota && (
                      <div className="rounded-lg border p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Quota Globale</span>
                          <span className="text-sm text-muted-foreground">
                            {formatBytes(storageQuota.used_bytes)} / {formatBytes(storageQuota.max_bytes)}
                          </span>
                        </div>
                        <Progress
                          value={storageQuota.max_bytes > 0 ? Math.min((storageQuota.used_bytes / storageQuota.max_bytes) * 100, 100) : 0}
                          className="h-2"
                        />
                        <p className="text-xs text-muted-foreground">
                          {storageQuota.max_bytes > 0
                            ? `${((storageQuota.used_bytes / storageQuota.max_bytes) * 100).toFixed(1)}% utilizzato`
                            : 'Quota non configurata'}
                        </p>
                      </div>
                    )}

                    <Separator />

                    <div className="rounded-lg border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/20 p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {storageSettings.recordings_enabled ? (
                            <Cloud className="h-5 w-5 text-blue-600" />
                          ) : (
                            <CloudOff className="h-5 w-5 text-gray-400" />
                          )}
                          <div>
                            <span className="font-semibold text-sm">Cloud Recording</span>
                            <p className="text-xs text-muted-foreground">Sincronizza le registrazioni chiamate su cloud</p>
                          </div>
                        </div>
                        <Switch
                          checked={storageSettings.recordings_enabled}
                          onCheckedChange={(checked) =>
                            setStorageSettings(s => ({ ...s, recordings_enabled: checked }))
                          }
                        />
                      </div>

                      {storageSettings.recordings_enabled && (
                        <div className="space-y-4 pl-8">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                              <Label className="text-xs">Max storage registrazioni</Label>
                              <select
                                value={storageSettings.recordings_max_bytes}
                                onChange={(e) =>
                                  setStorageSettings(s => ({ ...s, recordings_max_bytes: Number(e.target.value) }))
                                }
                                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                              >
                                {STORAGE_SIZE_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-xs">Retention registrazioni</Label>
                              <select
                                value={storageSettings.recordings_retention_days}
                                onChange={(e) =>
                                  setStorageSettings(s => ({ ...s, recordings_retention_days: Number(e.target.value) }))
                                }
                                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                              >
                                {RETENTION_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <Separator />

                    <div className="space-y-2">
                      <Label className="text-xs">Soglia alert quota (%)</Label>
                      <div className="flex items-center gap-4">
                        <select
                          value={storageSettings.alert_threshold_pct}
                          onChange={(e) =>
                            setStorageSettings(s => ({ ...s, alert_threshold_pct: Number(e.target.value) }))
                          }
                          className="flex h-9 w-48 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                        >
                          <option value={70}>70%</option>
                          <option value={80}>80%</option>
                          <option value={90}>90%</option>
                          <option value={95}>95%</option>
                        </select>
                        <p className="text-xs text-muted-foreground">
                          Ricevi una notifica quando lo spazio utilizzato supera questa soglia
                        </p>
                      </div>
                    </div>

                    <Button
                      onClick={handleSaveStorageSettings}
                      disabled={isSavingStorage}
                      className="w-full sm:w-auto"
                    >
                      {isSavingStorage ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Salvataggio...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Salva Impostazioni Storage
                        </>
                      )}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
